import { Box, CircularProgress, Tooltip } from '@mui/material';
import EpBlueBackIcon from '@root/assets/svg/ep_back.svg';
import WorkloadVulnerabilities from '@root/assets/svg/Kubernetes/WorkloadVulnerabilities';
import { SeverityWithText } from '@root/components/bootstrapTable/badges/severity';
import { axiosHelper } from '@root/services/axios';
import { isAiModelUnavailable, notifyAiModelUnavailable } from '@root/utils/aiModelUnavailable';
import { AlertTriangle, Check, CheckCircle, Copy, Lightbulb, Package, Terminal } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';

const styles = {
  container: {
    width: '100%',
    height: 'calc(100vh - 100px)',
    overflow: 'auto',
    padding: '20px',
    fontFamily: 'Manrope',
  },
  backButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '8px 12px',
    backgroundColor: '#f3f4f6',
    color: '#374151',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    fontSize: '13px',
    fontWeight: 600,
    fontFamily: 'Manrope',
    marginBottom: '16px',
    width: 'fit-content',
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '400px',
    gap: '16px',
  },
  loadingText: {
    fontSize: '14px',
    color: '#6b7280',
    fontFamily: 'Manrope',
  },
  errorContainer: {
    padding: '16px',
    borderRadius: '12px',
    backgroundColor: '#fef2f2',
    border: '1px solid #fecaca',
    display: 'flex',
    alignItems: 'flex-start',
    gap: '12px',
    marginBottom: '20px',
  },
  errorText: {
    fontSize: '14px',
    color: '#b91c1c',
    fontFamily: 'Manrope',
  },
  overviewCard: {
    // backgroundColor: '#f9fafb',
    border: '1px solid #e5e7eb',
    borderRadius: '12px',
    padding: '20px',
    marginBottom: '24px',
  },
  overviewTitle: {
    fontSize: '18px',
    fontWeight: 600,
    color: '#1a202c',
    marginBottom: '12px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  overviewContent: {
    display: 'grid',
    gap: '12px',
  },
  overviewItem: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '8px',
  },
  overviewLabel: {
    fontSize: '13px',
    fontWeight: 600,
    color: '#374151',
    minWidth: '120px',
  },
  overviewValue: {
    fontSize: '13px',
    color: '#4b5563',
    flex: 1,
  },
  severityBadge: {
    display: 'inline-block',
    padding: '4px 12px',
    borderRadius: '12px',
    fontSize: '12px',
    fontWeight: 600,
    textTransform: 'uppercase',
  },
  remediationSection: {
    marginBottom: '24px',
  },
  sectionTitle: {
    fontSize: '16px',
    fontWeight: 600,
    color: '#1a202c',
    marginBottom: '16px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  stepCard: {
    backgroundColor: 'white',
    border: '1px solid #e5e7eb',
    borderRadius: '12px',
    padding: '20px',
    marginBottom: '16px',
  },
  stepHeader: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '12px',
    marginBottom: '16px',
  },
  stepNumber: {
    width: '32px',
    height: '32px',
    borderRadius: '50%',
    backgroundColor: '#1e293b',
    color: 'white',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '14px',
    fontWeight: 600,
    flexShrink: 0,
  },
  stepContent: {
    flex: 1,
  },
  stepCategory: {
    fontSize: '14px',
    fontWeight: 600,
    color: '#1e293b',
    marginBottom: '8px',
  },
  stepDescription: {
    fontSize: '13px',
    color: '#4b5563',
    lineHeight: '1.6',
    marginBottom: '16px',
  },
  componentInfo: {
    backgroundColor: '#f3f4f6',
    padding: '12px',
    borderRadius: '8px',
    marginBottom: '16px',
  },
  componentRow: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: '6px',
    fontSize: '13px',
  },
  componentLabel: {
    fontWeight: 600,
    color: '#374151',
  },
  componentValue: {
    color: '#4b5563',
  },
  explanation: {
    backgroundColor: '#eff6ff',
    border: '1px solid #bfdbfe',
    borderRadius: '8px',
    padding: '12px',
    marginTop: '16px',
    display: 'flex',
    gap: '8px',
    alignItems: 'flex-start',
  },
  explanationIcon: {
    marginTop: '2px',
    flexShrink: 0,
  },
  explanationText: {
    fontSize: '13px',
    color: '#1e40af',
    lineHeight: '1.5',
  },
  customCommandBlock: {
    position: 'relative',
    backgroundColor: 'linear-gradient(135deg, #1f2937 0%, #111827 100%)',
    borderRadius: '12px',
    border: '1px solid #e5e7eb',
    overflow: 'hidden',
    boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1)',
    marginBottom: '12px',
  },
  customCommandHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '4px 8px',
    background: 'linear-gradient(135deg, #374151 0%, #1f2937 100%)',
    borderBottom: '1px solid #374151',
  },
  customCommandHeaderLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  customCommandHeaderTitle: {
    fontSize: '12px',
    fontWeight: '500',
    color: '#d1d5db',
    fontFamily: 'Manrope',
  },
  customCopyButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '6px 4px',
    color: '#d1d5db',
    // backgroundColor: '#374151',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    fontSize: '12px',
    fontWeight: '500',
    fontFamily: 'Manrope',
  },
  customCommandPre: {
    padding: '16px',
    fontSize: '13px',
    color: '#f3f4f6',
    fontFamily: 'Manrope',
    overflowX: 'auto',
    lineHeight: '1.5',
    margin: 0,
    whiteSpace: 'pre-wrap',
    maxHeight: '120px',
    background: 'linear-gradient(135deg, #1f2937 0%, #111827 100%)',
  },
  validationSection: {
    marginBottom: '24px',
  },
  validationList: {
    listStyleType: 'none',
    paddingLeft: '24px',
    margin: 0,
  },
  validationCard: {
    // backgroundColor: '#f0fdf4',
    border: '1px solid var(--border-color-light)',
    borderRadius: '8px',
    padding: '12px 16px',
    marginBottom: '8px',
    fontSize: '13px',
    // color: '#14532d',
    lineHeight: '1.6',
    display: 'list-item',
  },
  workloadSection: {
    marginBottom: '24px',
  },
  workloadList: {
    listStyleType: 'none',
    paddingLeft: '24px',
    margin: 0,
  },
  workloadCard: {
    // backgroundColor: '#f0f9ff',
    border: '1px solid var(--border-color-light)',
    borderRadius: '8px',
    padding: '12px 16px',
    marginBottom: '8px',
    fontSize: '13px',
    // color: '#0c4a6e',
    lineHeight: '1.6',
    display: 'list-item',
  },
};

// Helper function to parse numbered commands split by \n
const parseNumberedCommands = commandString => {
  if (!commandString) return [];

  // Split by \n (handles both "1. Text\n2." and "1. Text\n 2." with or without spaces)
  const lines = commandString
    .split('\n') // Use '\n' not '\\n' to match actual newline character
    .map(line => line.trim()) // Trim each line to remove leading/trailing spaces
    .filter(line => line.length > 0); // Remove empty lines
  console.log('Lines after split:', lines);
  const parsedSteps = [];

  lines.forEach(line => {
    // Match pattern: "1. Description: `command`" or "1. Description `command`"
    const match = line.match(/^(\d+)\.\s+(.+?):\s*`([^`]+)`/);

    if (match) {
      // Pattern: "1. Description: `command`"
      parsedSteps.push({
        number: match[1],
        description: match[2].trim(),
        command: match[3].trim(),
      });
    } else {
      // Try alternative pattern without colon: "1. Description `command`"
      const altMatch = line.match(/^(\d+)\.\s+(.+?)\s+`([^`]+)`/);
      if (altMatch) {
        parsedSteps.push({
          number: altMatch[1],
          description: altMatch[2].trim(),
          command: altMatch[3].trim(),
        });
      } else {
        // Try pattern with just description, no command
        const descMatch = line.match(/^(\d+)\.\s+(.+)$/);
        if (descMatch) {
          parsedSteps.push({
            number: descMatch[1],
            description: descMatch[2].trim(),
            command: null,
          });
        }
      }
    }
  });

  console.log('Parsed Steps:', parsedSteps);
  return parsedSteps;
};

// Custom Command Block Component
const CustomCommandBlock = ({ command }) => {
  const [copied, setCopied] = useState(false);

  const copyToClipboard = useCallback(async () => {
    try {
      if (navigator.clipboard) {
        await navigator.clipboard.writeText(command);
      } else {
        const textArea = document.createElement('textarea');
        textArea.value = command;
        textArea.style.position = 'fixed';
        textArea.style.opacity = '0';
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  }, [command]);

  return (
    <div style={styles.customCommandBlock}>
      <div style={styles.customCommandHeader}>
        <div style={styles.customCommandHeaderLeft}>
          <Terminal size={16} color="#10b981" />
          <span style={styles.customCommandHeaderTitle}>Terminal</span>
        </div>
        <Tooltip title={copied ? 'Copied!' : 'Copy'}>
          <button
            data-testid="click-k8s-pages-sideoverlay-vulnerabilityremediationai-copytoclipboard-376"
            data-clickable="true"
            onClick={copyToClipboard}
            style={styles.customCopyButton}
            onMouseEnter={e => {
              e.currentTarget.style.backgroundColor = '#4b5563';
              e.currentTarget.style.color = 'white';
            }}
            onMouseLeave={e => {
              e.currentTarget.style.backgroundColor = '#374151';
              e.currentTarget.style.color = '#d1d5db';
            }}>
            {copied ? (
              <>
                <Check size={14} color="#10b981" />
              </>
            ) : (
              <>
                <Copy size={14} />
              </>
            )}
          </button>
        </Tooltip>
      </div>
      <pre style={styles.customCommandPre} className="CustomScrollbar">
        {command}
      </pre>
    </div>
  );
};

const VulnerabilityRemediationAi = ({ selectedRow, detailValue, onClose }) => {
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const getRemediationAnalysis = async () => {
    setLoading(true);
    setError('');
    setAnalysis(null);

    try {
      const payload = {
        vulnerability_id: selectedRow?.cve_id,
        ...(selectedRow?.workload_id && { workload_id: selectedRow?.workload_id }),
        ...(selectedRow?.component && { artifact_id: selectedRow?.component }),
        ...(selectedRow?.image && { image_id: selectedRow?.image }),
        provider: 'google',
      };

      const response = await axiosHelper.axiosPost('v1/aiprovider/k8s-vulnerability-remediation', payload, true);

      if (response.status === 429) {
        setError('Rate limit exceeded. Please try again later.');
        throw new Error(response.data.error?.message || 'Rate limit exceeded');
      }

      if (response.status !== 200) {
        throw new Error(response.data.error?.message || 'Failed to get remediation analysis');
      }

      if (isAiModelUnavailable(response?.data)) {
        setError(notifyAiModelUnavailable(response.data));
        return;
      }

      const data = response?.data?.data;

      if (!data) {
        throw new Error('Invalid response format');
      }

      setAnalysis(data);
    } catch (err) {
      console.error('Remediation analysis error:', err);
      setError(err.message || 'Failed to get remediation analysis. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getRemediationAnalysis();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRow?.cve_id]);

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.loadingContainer}>
          <CircularProgress size={40} style={{ color: '#1e293b' }} />
          <div style={styles.loadingText}>Analyzing vulnerability and generating remediation steps...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.container}>
        <div style={styles.errorContainer}>
          <AlertTriangle size={20} color="#dc2626" style={{ marginTop: '2px', flexShrink: 0 }} />
          <div>
            <div style={{ fontWeight: 600, marginBottom: '4px', color: '#991b1b' }}>Error</div>
            <div style={styles.errorText}>{error}</div>
          </div>
        </div>
      </div>
    );
  }

  if (!analysis) {
    return (
      <div style={styles.container}>
        <div style={styles.errorContainer}>
          <AlertTriangle size={20} color="#dc2626" style={{ marginTop: '2px', flexShrink: 0 }} />
          <div style={styles.errorText}>No remediation data available</div>
        </div>
      </div>
    );
  }

  const overview = analysis.vulnerability_overview;
  const steps = analysis.remediation_steps || [];
  const validationSteps = analysis.validation_steps || [];
  const workloadRecommendations = analysis.workload_recommendations || analysis.workload_vulnerabilities || [];

  return (
    <div style={styles.container} className="CustomScrollbar">
      {/* Back Button */}
      <div className="absolute top-0 left-0">
        <img
          src={EpBlueBackIcon}
          className="relative top-[45px] left-[18px] h-[24px] w-[24px]"
          style={{ cursor: 'pointer' }}
          data-testid="click-k8s-pages-sideoverlay-vulnerabilityremediationai-onclose-504"
          data-clickable="true"
          onClick={onClose}
          alt="Back"
        />
      </div>

      {/* Vulnerability Overview */}
      {overview && (
        <div style={styles.overviewCard}>
          <div style={styles.overviewTitle}>
            <WorkloadVulnerabilities height="20px" width="20px" />
            Vulnerability Overview
          </div>
          <div style={styles.overviewContent}>
            <div style={styles.overviewItem}>
              <div style={styles.overviewLabel}>Title:</div>
              <div style={styles.overviewValue}>{overview.title || detailValue?.cve_id || '-'}</div>
            </div>
            <div style={styles.overviewItem}>
              <div style={styles.overviewLabel}>Severity:</div>
              <div>
                <SeverityWithText propsValue={overview.severity || detailValue?.severity} />
              </div>
            </div>
            {overview.cvss_score && (
              <div style={styles.overviewItem}>
                <div style={styles.overviewLabel}>CVSS Score:</div>
                <div style={styles.overviewValue}>{overview.cvss_score}</div>
              </div>
            )}
            {overview.description && (
              <div style={styles.overviewItem}>
                <div style={styles.overviewLabel}>Description:</div>
                <div style={styles.overviewValue}>{overview.description}</div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Remediation Steps */}
      <div style={styles.remediationSection}>
        <div style={styles.sectionTitle}>
          <Lightbulb size={20} color="#1e293b" />
          Remediation Steps
        </div>

        {steps.length === 0 ? (
          <div style={styles.errorContainer}>
            <AlertTriangle size={20} color="#f59e0b" style={{ marginTop: '2px', flexShrink: 0 }} />
            <div style={{ ...styles.errorText, color: '#92400e' }}>
              No remediation steps available for this vulnerability
            </div>
          </div>
        ) : (
          steps.map((step, index) => (
            <div key={index} style={styles.stepCard}>
              <div style={styles.stepHeader}>
                <div style={styles.stepNumber}>{index + 1}</div>
                <div style={styles.stepContent}>
                  <div style={styles.stepCategory}>
                    <Package size={16} style={{ display: 'inline', marginRight: '6px' }} />
                    {step.category || 'Remediation Step'}
                  </div>
                  {step.description && <div style={styles.stepDescription}>{step.description}</div>}

                  {/* Component Information */}
                  {step.component && (
                    <div style={styles.componentInfo}>
                      {step.component.name && (
                        <div style={styles.componentRow}>
                          <span style={styles.componentLabel}>Component:</span>
                          <span style={styles.componentValue}>{step.component.name}</span>
                        </div>
                      )}
                      {step.component.current_version && (
                        <div style={styles.componentRow}>
                          <span style={styles.componentLabel}>Current Version:</span>
                          <span style={styles.componentValue}>{step.component.current_version}</span>
                        </div>
                      )}
                      {step.component.required_version && (
                        <div style={styles.componentRow}>
                          <span style={styles.componentLabel}>Required Version:</span>
                          <span style={styles.componentValue}>{step.component.required_version}</span>
                        </div>
                      )}
                      {step.component.location && (
                        <div style={styles.componentRow}>
                          <span style={styles.componentLabel}>Location:</span>
                          <span style={styles.componentValue}>{step.component.location}</span>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Command Block - Handle numbered commands split by \n */}
                  {step.command && (
                    <>
                      {(() => {
                        const numberedSteps = parseNumberedCommands(step.command);

                        if (numberedSteps.length > 0) {
                          // Render each numbered step separately
                          return (
                            <Box marginTop={'12px'}>
                              {numberedSteps.map((cmdStep, cmdIdx) => (
                                <Box key={cmdIdx} marginBottom={'16px'}>
                                  <div
                                    style={{
                                      fontSize: '13px',
                                      fontWeight: 600,
                                      color: '#374151',
                                      marginBottom: '8px',
                                    }}>
                                    {cmdStep.number}. {cmdStep.description}
                                  </div>
                                  {cmdStep.command && (
                                    <CustomCommandBlock
                                      command={cmdStep.command}
                                      commandId={`remediation-${index}-${cmdIdx}`}
                                    />
                                  )}
                                </Box>
                              ))}
                            </Box>
                          );
                        } else {
                          // Render single command block if not numbered
                          return (
                            <Box marginTop={'12px'}>
                              <CustomCommandBlock command={step.command} commandId={`remediation-${index}`} />
                            </Box>
                          );
                        }
                      })()}
                    </>
                  )}

                  {/* Explanation */}
                  {step.explanation && (
                    <div style={styles.explanation}>
                      <CheckCircle size={16} color="#1e40af" style={styles.explanationIcon} />
                      <div style={styles.explanationText}>{step.explanation}</div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Workload Recommendations / Vulnerabilities */}
      {workloadRecommendations.length > 0 && (
        <div style={styles.workloadSection}>
          <div style={styles.sectionTitle}>
            <Package size={20} color="#1e293b" />
            Workload Recommendations
          </div>
          <ul style={styles.workloadList}>
            {workloadRecommendations.map((recommendation, index) => (
              <li key={index} style={styles.workloadCard}>
                {recommendation}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Validation Steps */}
      {validationSteps.length > 0 && (
        <div style={styles.validationSection}>
          <div style={styles.sectionTitle}>
            <CheckCircle size={20} color="#1e293b" />
            Validation Steps
          </div>
          <ul style={styles.validationList}>
            {validationSteps.map((validation, index) => (
              <li key={index} style={styles.validationCard}>
                {validation}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default VulnerabilityRemediationAi;
